import { useState, useEffect } from "react";
import {
  uploadProcessFile,
  getUpdateRunList,
  deleteUpdateRun,
} from "../../api/process";

import BoardTable from "../../components/common/board/BoardTable";
import TableCell from "../../components/common/ui/TableCell";
import ConfirmModal from "../../components/common/ui/ConfirmModal";
import PageHeader from "../../components/common/ui/PageHeader";
import { formatDate } from "../../utils/date";

export default function Upload() {
  const [file, setFile] = useState(null);
  const [withBaseline, setWithBaseline] = useState(false);
  const [baselineCode, setBaselineCode] = useState("");
  const [loading, setLoading] = useState(false);

  const [actualRuns, setActualRuns] = useState([]);
  const [baselineRuns, setBaselineRuns] = useState([]);
  const [cutoffDate, setCutoffDate] = useState("");
  const [tab, setTab] = useState("A");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [targetRun, setTargetRun] = useState(null);

  const [sourceType, setSourceType] = useState("P6");
  const [acEqBa, setAcEqBa] = useState(false);

  const handleDeleteClick = (run) => {
    setTargetRun(run);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!targetRun) return;

    try {
      await deleteUpdateRun(targetRun.id);
      alert("삭제되었습니다.");
      loadHistory();
    } catch (e) {
      alert("삭제 실패: " + e.message);
    } finally {
      setConfirmOpen(false);
      setTargetRun(null);
    }
  };

  const currentRuns = tab === "A" ? actualRuns : baselineRuns;

  const columns = [
    
    {
      key: "main",
      header: tab === "B" ? "Revision" : "실적 기준일",
      width: "120px",
      align: "center",
      render: (row) =>
        tab === "A"
          ? row.data_date
            ? formatDate(row.data_date)
            : "-"
          : `Rev.${row.revision || "-"}`,
    },
    {
      key: "uploaded_by",
      header: "작성자",
      width: "120px",
      align: "center",
      render: (row) => row.uploaded_by?.name || "-",
    },
    {
      key: "uploaded_at",
      header: "업로드일",
      width: "140px",
      align: "center",
      render: (row) => (row.uploaded_at ? formatDate(row.uploaded_at) : "-"),
    },
    {
      key: "actions",
      header: "관리",
      width: "80px",
      align: "center",
      render: (row) => (
        <button
          className="text-red-500 hover:text-red-700 text-xs font-medium"
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteClick(row);
          }}
        >
          삭제
        </button>
      ),
    },
  ];


  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const runs = await getUpdateRunList();
      const actual = runs.filter((r) => r.run_type === "A");
      const baseline = runs.filter((r) => r.run_type === "B");

      setActualRuns(actual.sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at)));
      setBaselineRuns(baseline.sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at)));
    } catch (e) {
      console.error("이력 로드 실패:", e);
    }
  };

  const handleUpload = async () => {
    if (!file) return alert("파일을 선택하세요.");
    if (withBaseline && !baselineCode.trim()) return alert("Baseline 버전명을 입력하세요.");

    setLoading(true);

    try {
      await uploadProcessFile(
        file,
        withBaseline,
        baselineCode,
        cutoffDate,
        sourceType,
        acEqBa
      );

      alert("업로드가 완료되었습니다.");
      loadHistory();
    } catch (e) {
      alert(`에러 발생: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      {/* ------------------------------ */}
      {/*      📌 Page Header 영역       */}
      {/* ------------------------------ */}
      <PageHeader>공정 실적 업로드</PageHeader>

      <div className="grid grid-cols-12 gap-6">
        {/* ------------------------------ */}
        {/*            LEFT FORM           */}
        {/* ------------------------------ */}
        <div className="col-span-12 lg:col-span-4">
          <div className="bg-white border rounded-lg p-6 space-y-4">

            <h3 className="text-subtitle">
              실적 업로드
            </h3>
            <p className="text-muted-sm">
              {sourceType === "P6" ? (
                <>Primavera P6 XML 공정 파일을 업로드하여 실적을 반영합니다.</>
              ) : (
                <>MS Project XML 파일을 업로드하여 실적을 반영합니다.</>
              )}
            </p>

            <table className="w-full text-sm border border-gray-200">
              <tbody>

                <tr>
                  <TableCell
                    label="파일 타입"
                    value={
                      <select
                        className="border rounded px-2 py-1 text-sm bg-white"
                        value={sourceType}
                        onChange={(e) => setSourceType(e.target.value)}
                      >
                        <option value="MS">Microsoft Project (MSP)</option>
                        <option value="P6">Primavera P6 (XML)</option>
                      </select>
                    }
                  />
                </tr>

                <tr>
                  <TableCell
                    label="실적 파일"
                    value={
                      <input
                        type="file"
                        accept=".xml"
                        onChange={(e) => setFile(e.target.files[0])}
                        className="w-full border rounded px-2 py-1 text-sm"
                      />
                    }
                  />
                </tr>

                <tr>
                  <TableCell
                    label="실적 기준일"
                    value={
                      <input
                        type="date"
                        className="border rounded px-2 py-1 text-sm"
                        value={cutoffDate}
                        onChange={(e) => setCutoffDate(e.target.value)}
                      />
                    }
                  />
                </tr>

                <tr>
                  <TableCell
                    label="Baseline 옵션"
                    value={
                      <div className="space-y-2">

                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={withBaseline}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setWithBaseline(checked);
                              if (checked) setAcEqBa(false);
                            }}
                          />
                          Baseline 정보가 포함된 경우
                        </label>

                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={acEqBa}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setAcEqBa(checked);
                              if (checked) setWithBaseline(false);
                            }}
                          />
                          Actual 내용을 Baseline에도 저장
                        </label>

                      </div>
                    }
                  />
                </tr>

                {(withBaseline || acEqBa) && (
                  <tr>
                    <TableCell
                      label="Revision"
                      value={
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-700">Rev.</span>
                          <input
                            type="text"
                            value={baselineCode}
                            onChange={(e) => setBaselineCode(e.target.value)}
                            className="flex-1 border rounded px-2 py-1 text-sm"
                          />
                        </div>
                      }
                    />
                  </tr>
                )}
              </tbody>
            </table>

            <button
              disabled={loading}
              onClick={handleUpload}
              className="w-full btn-create py-2"
            >
              {loading ? "업로드 중…" : "업로드"}
            </button>

          </div>
        </div>

        {/* ------------------------------ */}
        {/*          RIGHT HISTORY         */}
        {/* ------------------------------ */}
        <div className="col-span-12 lg:col-span-8">
          <div className="bg-white border rounded-lg p-6">

            <div className="flex items-center justify-between mb-4">
              <h3 className="text-subtitle">
                업로드 이력
              </h3>


            </div>
            {/* 탭 UI (요청한 스타일로 변경) */}
            <div className="flex gap-6 mb-4 border-b">
              <button
                className={`px-4 py-2 -mb-px text-sm font-medium ${tab === "A"
                  ? "border-b-2 border-blue-600 text-blue-600"
                  : "text-gray-500 hover:text-gray-700"
                  }`}
                onClick={() => setTab("A")}
              >
                실적
              </button>

              <button
                className={`px-4 py-2 -mb-px text-sm font-medium ${tab === "B"
                  ? "border-b-2 border-blue-600 text-blue-600"
                  : "text-gray-500 hover:text-gray-700"
                  }`}
                onClick={() => setTab("B")}
              >
                기준선
              </button>
            </div>

            <BoardTable
              columns={columns}
              rows={currentRuns}
              keyField="id"
              sortable={false}
              emptyText={
                tab === "A"
                  ? "실적 업로드 이력이 없습니다."
                  : "기준선 업로드 이력이 없습니다."
              }
            />
          </div>
        </div>
      </div>

      {/* ------------------------------ */}
      {/*       삭제 확인 모달            */}
      {/* ------------------------------ */}
      <ConfirmModal
        open={confirmOpen}
        title="삭제 확인"
        description={
          targetRun
            ? `선택한 ${targetRun.run_type === "A" ? "실적" : "기준선"} 데이터를 삭제하시겠습니까?`
            : ""
        }
        cancelText="아니오"
        confirmText="예"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmOpen(false)}
        onClose={() => setConfirmOpen(false)}
      />
    </div>
  );
}
